import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../api/client";

function strength(pw) {
  if (!pw) return { label: "", tone: "" };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;

  if (score <= 2) return { label: "Weak", tone: "var(--es-bad)" };
  if (score === 3) return { label: "Fair", tone: "var(--es-warn)" };
  if (score === 4) return { label: "Good", tone: "var(--es-teal-600)" };
  return { label: "Strong", tone: "var(--es-ok)" };
}

export default function ResetPassword() {
  const { userId, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const pwStrength = useMemo(() => strength(password), [password]);

  // A link missing either half can never succeed, so say so up front rather
  // than letting the user type a password and then fail.
  const linkIncomplete = !userId || !token;

  async function handleSubmit(e) {
    e.preventDefault();
    setBanner(null);

    const next = {};
    if (!password) next.password = "Choose a new password.";
    else if (password.length < 8) next.password = "Use at least 8 characters.";
    if (password !== confirmPassword) next.confirmPassword = "The two passwords do not match.";

    setErrors(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    try {
      await api.post(`/users/reset-password/${userId}/${token}`, {
        password,
        confirmPassword,
      });
      setDone(true);
    } catch (err) {
      setBanner({
        kind: "error",
        text:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "That reset link is invalid or has expired. Request a new one.",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="es-auth">
      <aside className="es-auth__aside">
        <div>
          <Link to="/" className="es-brand" style={{ color: "#fff" }}>
            <span className="es-brand__mark" aria-hidden="true">ES</span>
            <span className="es-brand__name">
              <strong>Edu</strong>
              <span style={{ color: "var(--es-sand-400)" }}>Special</span>
            </span>
          </Link>
        </div>

        <div>
          <h2 className="es-auth__aside-title">Choose a new password</h2>
          <p>
            Reset links are single-use and expire an hour after they are sent.
            If yours has lapsed you can request another from the sign-in page.
          </p>
        </div>

        <p style={{ fontSize: "0.85rem", color: "#a9c5c2" }}>
          Never share your password with anyone, including our staff.
        </p>
      </aside>

      <div className="es-auth__panel">
        <div className="es-auth__form es-rise">
          {done ? (
            <>
              <h1 className="es-h1" style={{ fontSize: "1.8rem" }}>Password updated</h1>
              <p className="es-lede" style={{ fontSize: "0.98rem" }}>
                You can now sign in with your new password.
              </p>
              <button
                type="button"
                className="es-btn es-btn--primary"
                style={{ width: "100%" }}
                onClick={() => navigate("/signin", { replace: true })}
              >
                Go to sign in
              </button>
            </>
          ) : (
            <>
              <Link to="/signin" className="es-auth__back">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m15 18-6-6 6-6" />
                </svg>
                Back to sign in
              </Link>

              <h1 className="es-h1" style={{ fontSize: "1.9rem" }}>Set a new password</h1>
              <p className="es-lede" style={{ marginBottom: "1.5rem", fontSize: "0.98rem" }}>
                Pick something you have not used here before.
              </p>

              {linkIncomplete && (
                <div className="es-alert es-alert--error" role="alert">
                  This reset link is incomplete. Request a new one from the
                  sign-in page.
                </div>
              )}

              {banner && (
                <div className="es-alert es-alert--error" role="alert">{banner.text}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="es-field">
                  <label className="es-label" htmlFor="rp-pw">New password</label>
                  <div style={{ position: "relative" }}>
                    <input
                      id="rp-pw"
                      type={showPw ? "text" : "password"}
                      className="es-input"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setErrors((p) => ({ ...p, password: undefined }));
                      }}
                      style={{ paddingRight: "4.25rem" }}
                      aria-invalid={errors.password ? "true" : undefined}
                      aria-describedby={errors.password ? "rp-pw-err" : "rp-pw-strength"}
                    />
                    <button
                      type="button"
                      className="es-linkbtn"
                      onClick={() => setShowPw((v) => !v)}
                      aria-pressed={showPw}
                      style={{ position: "absolute", right: "0.75rem", top: "50%", transform: "translateY(-50%)" }}
                    >
                      {showPw ? "Hide" : "Show"}
                    </button>
                  </div>

                  {errors.password ? (
                    <p className="es-error" id="rp-pw-err">{errors.password}</p>
                  ) : (
                    <p className="es-hint" id="rp-pw-strength" aria-live="polite">
                      {password ? (
                        <>Strength: <strong style={{ color: pwStrength.tone }}>{pwStrength.label}</strong></>
                      ) : (
                        "At least 8 characters."
                      )}
                    </p>
                  )}
                </div>

                <div className="es-field">
                  <label className="es-label" htmlFor="rp-pw2">Confirm new password</label>
                  <input
                    id="rp-pw2"
                    type={showPw ? "text" : "password"}
                    className="es-input"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors((p) => ({ ...p, confirmPassword: undefined }));
                    }}
                    aria-invalid={errors.confirmPassword ? "true" : undefined}
                    aria-describedby={errors.confirmPassword ? "rp-pw2-err" : undefined}
                  />
                  {errors.confirmPassword && (
                    <p className="es-error" id="rp-pw2-err">{errors.confirmPassword}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="es-btn es-btn--primary"
                  style={{ width: "100%" }}
                  disabled={loading || linkIncomplete}
                >
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
